﻿import { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity, LayoutAnimation, Platform, UIManager, Alert } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  Trash2, Video, ChevronDown, ChevronUp,
  Dumbbell, CheckCircle, GripVertical, X, Check,
} from 'lucide-react-native';
import { lookupExerciseInfo } from '@/constants/exerciseDatabase';
import SetRow from './SetRow';
import ExerciseHeader from './ExerciseHeader';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';

import LiftHistory from '@/components/LiftHistory';
import { Exercise } from '@/types/database';
import { getOverloadSuggestion, type OverloadSuggestion } from '@/lib/progressiveOverload';
import { getLastPerformance, type LastPerformance } from '@/lib/lastWorkoutData';
import { useUnits } from '@/lib/useUnits';

const SIMPLE_MODE_KEY = 'record:simpleMode';

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

export type EntryMode = 'weight' | 'time';
export type EquipmentType = 'barbell' | 'dumbbell' | 'machine' | 'cable' | 'bodyweight';

export const EQUIPMENT_OPTIONS: { id: EquipmentType; label: string }[] = [
  { id: 'barbell', label: 'Barbell' },
  { id: 'dumbbell', label: 'Dumbbell' },
  { id: 'machine', label: 'Machine' },
  { id: 'cable', label: 'Cable' },
  { id: 'bodyweight', label: 'Bodyweight' },
];

/** Infer default equipment from exercise name */
export function inferEquipment(name: string): EquipmentType {
  const n = name.toLowerCase();
  if (/dumbbell|goblet|hammer curl|concentration curl/.test(n)) return 'dumbbell';
  if (/cable|lat pulldown|tricep pushdown|face pull|seated row|leg curl|leg extension|machine|hack squat|chest press/.test(n)) return 'cable';
  if (/leg press|calf raise/.test(n)) return 'machine';
  if (/pull-up|chin-up|push-up|dip|bodyweight|pistol|muscle-up|inverted row|hanging|burpee|plank|dead bug/.test(n)) return 'bodyweight';
  return 'barbell';
}

export interface SetData {
  weight: string;
  reps: string;
  time?: string; // optional seconds (e.g. farmer's carry hold)
  is_warmup?: boolean;
  rpe?: string; // per-set RPE (6-10, half increments)
}

export interface ExerciseEntry {
  id: string;
  exercise: Exercise | null;
  weight: string;        // kept for backward compat / single-set display
  reps: string;          // kept for backward compat
  sets: string;          // total set count (derived from setDetails.length when present)
  setDetails?: SetData[]; // per-set weight/reps
  isPR: boolean;
  videoUri: string | null;
  notes: string;
  rpe?: string; // Rate of Perceived Exertion (1-10)
  mode?: EntryMode; // weight (default) or time
  duration?: string; // seconds, used when mode=time
  equipment?: EquipmentType; // equipment type override
  supersetGroupId?: string; // superset group identifier
}

const RPE_VALUES = ['6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10'];

interface Props {
  entry: ExerciseEntry;
  index: number;
  bodyPartGridWidth?: number;
  onBodyPartGridLayout?: (width: number) => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, updates: Partial<ExerciseEntry>) => void;
  onSelectBodyPart: (entryId: string, bodyPart: string) => void;
  onShowVideoOptions: (entryId: string) => void;
  onSetLogged?: () => void; // callback after a set is confirmed (triggers rest timer)
  totalExercises?: number; // total number of exercises (for showing reorder buttons)
  onMoveUp?: () => void; // move this exercise up
  onMoveDown?: () => void; // move this exercise down
}

function calculateVolume(weight: string, reps: string, sets: string) {
  const w = parseFloat(weight) || 0;
  const r = parseInt(reps) || 0;
  const s = parseInt(sets) || 1;
  return w * r * s;
}

export default function ExerciseEntryCard({
  entry,
  index,
  bodyPartGridWidth,
  onBodyPartGridLayout,
  onRemove,
  onUpdate,
  onSelectBodyPart,
  onShowVideoOptions,
  onSetLogged,
  totalExercises = 1,
  onMoveUp,
  onMoveDown,
}: Props) {
  const { theme } = useTheme();
  const { profile } = useAuth();

  const { displayWeight, userUnits } = useUnits();

  const [suggestion, setSuggestion] = useState<OverloadSuggestion | null>(null);
  const [lastPerf, setLastPerf] = useState<LastPerformance | null>(null);
  const [rpeOpenIdx, setRpeOpenIdx] = useState<number | null>(null);
  const [showExInfo, setShowExInfo] = useState(false);
  const [simpleMode, setSimpleMode] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  // Load simple mode preference from AsyncStorage
  useEffect(() => {
    AsyncStorage.getItem(SIMPLE_MODE_KEY).then((val) => {
      // Default true for new users (val === null)
      setSimpleMode(val === null ? true : val === 'true');
    }).catch(() => {});
  }, []);

  const toggleSimpleMode = () => {
    const next = !simpleMode;
    setSimpleMode(next);
    AsyncStorage.setItem(SIMPLE_MODE_KEY, String(next)).catch(() => {});
  };

  const exerciseInfo = entry.exercise ? lookupExerciseInfo(entry.exercise.name) : undefined;

  // Detect time-based exercises (show time input instead of reps)
  const isTimeBased = (() => {
    const name = (entry.exercise?.name ?? '').toLowerCase();
    // Static holds and carries
    if (name.includes('farmer') && (name.includes('carry') || name.includes('walk') || name.includes('hold'))) return true;
    if (name.includes('static hold') || name.includes('dead hang') || name.includes('deadhang') || name.includes('bar hang') || name.includes('hang')) return true;
    // Core time-based
    if (name.includes('plank') || name.includes('l-sit') || name.includes('l sit')) return true;
    // Cardio/conditioning that's typically timed
    if (name.includes('battle rope') || name.includes('assault bike') || name.includes('rowing') || name.includes('rower')) return true;
    // Explicit mode override
    if (entry.mode === 'time') return true;
    return false;
  })();
  
  // Dead hang is bodyweight-only timed (no weight input needed)
  const isDeadHang = (() => {
    const name = (entry.exercise?.name ?? '').toLowerCase();
    return name.includes('dead hang') || name.includes('deadhang') || name === 'hang' || name.includes('bar hang') || name.includes('passive hang');
  })();

  // Farmer's carry specifically shows "Total Load" label
  const isFarmersCarry = (() => {
    const name = (entry.exercise?.name ?? '').toLowerCase();
    return name.includes('farmer') && (name.includes('carry') || name.includes('walk') || name.includes('hold'));
  })();

  // Auto-set equipment from exercise name when exercise changes
  useEffect(() => {
    if (entry.exercise && !entry.equipment) {
      const inferred = inferEquipment(entry.exercise.name);
      onUpdate(entry.id, { equipment: inferred });
    }
  }, [entry.exercise?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!profile?.id || !entry.exercise?.name) {
      setSuggestion(null);
      return;
    }
    let cancelled = false;
    getOverloadSuggestion(profile.id, entry.exercise.name).then((s) => {
      if (!cancelled) setSuggestion(s);
    });
    return () => { cancelled = true; };
  }, [profile?.id, entry.exercise?.name]);

  // Fetch last performance for this exercise
  useEffect(() => {
    if (!profile?.id || !entry.exercise?.name) {
      setLastPerf(null);
      return;
    }
    let cancelled = false;
    getLastPerformance(profile.id, entry.exercise.name).then((lp) => {
      if (!cancelled) setLastPerf(lp);
    });
    return () => { cancelled = true; };
  }, [profile?.id, entry.exercise?.name]);

  const applyLastPerformance = () => {
    if (!lastPerf) return;
    const details = lastPerf.sets.map((s) => {
      // Convert weight from workout units to user's current display units
      const convertedWeight = displayWeight(s.weight, lastPerf.units);
      return {
        weight: String(convertedWeight),
        reps: String(s.reps),
      };
    });
    onUpdate(entry.id, {
      setDetails: details,
      weight: details[0]?.weight ?? '',
      reps: details[0]?.reps ?? '',
      sets: String(details.length),
    });
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    const updates: Partial<ExerciseEntry> = {};
    if (suggestion.suggestedWeight > 0) {
      updates.weight = String(suggestion.suggestedWeight);
    }
    updates.reps = String(suggestion.suggestedReps);
    // Also update the first set detail if present
    if (entry.setDetails && entry.setDetails.length > 0) {
      const details = [...entry.setDetails];
      details[0] = {
        ...details[0],
        weight: suggestion.suggestedWeight > 0 ? String(suggestion.suggestedWeight) : details[0].weight,
        reps: String(suggestion.suggestedReps),
      };
      updates.setDetails = details;
    }
    onUpdate(entry.id, updates);
  };

  return (
    <View
      style={[
        styles.entryCard,
        {
          backgroundColor: theme.colors.surface,
          borderColor: theme.colors.border,
        },
      ]}>
      <View style={styles.cardHeaderRow}>
        <View style={{ flex: 1 }}>
          <ExerciseHeader
            index={index}
            totalExercises={totalExercises}
            theme={theme}
            onRemove={() => onRemove(entry.id)}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
          />
        </View>
        {entry.exercise && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <TouchableOpacity onPress={() => setCollapsed(!collapsed)} hitSlop={8} accessibilityRole="button" accessibilityLabel={collapsed ? 'Expand exercise' : 'Collapse exercise'}>
              <Check size={20} color={collapsed ? theme.colors.accent : theme.colors.textTertiary} />
            </TouchableOpacity>
            <TouchableOpacity onPress={toggleSimpleMode} hitSlop={8}>
              <Text style={[styles.simpleModeToggle, { color: theme.colors.textTertiary }]}>
                {simpleMode ? 'Advanced' : 'Simple'}
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {!entry.exercise ? (
        <TouchableOpacity
          style={[
            styles.selectExerciseButton,
            {
              backgroundColor: theme.colors.surfaceVariant,
              borderColor: theme.colors.accent,
            },
          ]}
          onPress={() => onSelectBodyPart(entry.id, '')}
          accessibilityRole="button"
          accessibilityLabel="Select exercise">
          <Dumbbell size={24} color={theme.colors.accent} />
          <Text style={[styles.selectExerciseText, { color: theme.colors.accent }]}>
            Select Exercise
          </Text>
        </TouchableOpacity>
      ) : (
        <View>
          <View style={[styles.selectedExercise, { borderColor: theme.colors.accent }]}>
            <View>
              <Text style={[styles.selectedExerciseName, { color: theme.colors.accent }]}>
                {entry.exercise.name}
              </Text>
              <Text style={[styles.exerciseCategory, { color: theme.colors.textSecondary }]}>
                {entry.exercise.body_part}
              </Text>
            </View>
            <TouchableOpacity
              onPress={() => onUpdate(entry.id, { exercise: null })}
              accessibilityRole="button"
              accessibilityLabel="Change exercise">
              <Text style={[styles.changeText, { color: theme.colors.textTertiary }]}>
                Change
              </Text>
            </TouchableOpacity>
          </View>

          {/* Collapsed summary line */}
          {collapsed && (
            <Text style={{ color: theme.colors.textSecondary, fontSize: 13, marginTop: 4, marginBottom: 4 }}>
              {entry.sets || '1'} set{(parseInt(entry.sets) || 1) !== 1 ? 's' : ''} × {entry.reps || '—'} reps
              {entry.weight ? ` @ ${entry.weight} ${userUnits}` : ''}
            </Text>
          )}

          {/* Full exercise content (hidden when collapsed) */}
          {!collapsed && (<>

          {/* Muscle badge + collapsible instructions */}
          {!simpleMode && exerciseInfo && (
            <View style={styles.exerciseInfoSection}>
              <TouchableOpacity
                style={styles.exerciseInfoToggle}
                onPress={() => {
                  LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
                  setShowExInfo(!showExInfo);
                }}>
                <View style={[styles.muscleBadge, { backgroundColor: theme.colors.accent + '20' }]}>
                  <Text style={[styles.muscleBadgeText, { color: theme.colors.accent }]}>
                    {exerciseInfo.primaryMuscle}
                  </Text>
                </View>
                {showExInfo
                  ? <ChevronUp size={16} color={theme.colors.textTertiary} />
                  : <ChevronDown size={16} color={theme.colors.textTertiary} />
                }
              </TouchableOpacity>
              {showExInfo && (
                <Text style={[styles.exerciseInstructions, { color: theme.colors.textSecondary }]}>
                  {exerciseInfo.instructions}
                </Text>
              )}
            </View>
          )}

          {/* Last performance inline display */}
          {!simpleMode && lastPerf && (() => {
            // Compare current top set vs last session top set
            const currentSets = entry.setDetails ?? [{ weight: entry.weight, reps: entry.reps }];
            const currentTop = Math.max(...currentSets.map(s => parseFloat(s.weight) || 0));
            const lastTop = Math.max(...lastPerf.sets.map(s => displayWeight(s.weight, lastPerf.units)));
            const delta = currentTop > 0 && lastTop > 0 ? Math.round((currentTop - lastTop) * 10) / 10 : null;
            const deltaStr = delta !== null
              ? delta > 0 ? `+${delta} ${userUnits} vs last top set` : delta < 0 ? `${delta} ${userUnits} vs last top set` : 'matched last top set'
              : null;
            const deltaColor = delta !== null && delta > 0 ? '#22c55e' : delta !== null && delta < 0 ? '#ef4444' : theme.colors.textTertiary;

            return (
              <TouchableOpacity
                style={[styles.lastPerfRow, { backgroundColor: theme.colors.surfaceVariant + '80', borderLeftWidth: 2, borderLeftColor: theme.colors.accent + '40' }]}
                onPress={applyLastPerformance}
                activeOpacity={0.6}>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.lastPerfLabel, { color: theme.colors.textTertiary }]}>{'LAST SESSION \u00B7 '}{lastPerf.date}</Text>
                  <Text style={[styles.lastPerfText, { color: theme.colors.textSecondary }]}>
                    {lastPerf.sets.map((s, i) => {
                      const convertedWeight = displayWeight(s.weight, lastPerf.units);
                      return `${convertedWeight}\u00D7${s.reps}`;
                    }).join('  ')}
                  </Text>
                  {suggestion && (
                    <Text style={[styles.lastPerfSuggestion, { color: theme.colors.accent }]}>
                      {`\u2192 Try ${suggestion.suggestedWeight > 0 ? suggestion.suggestedWeight + ' \u00D7 ' + suggestion.suggestedReps : suggestion.suggestedReps + ' reps'}`}
                    </Text>
                  )}
                </View>
                {deltaStr && (
                  <Text style={[styles.deltaText, { color: deltaColor }]}>{deltaStr}</Text>
                )}
              </TouchableOpacity>
            );
          })()}
          {/* Equipment type selector */}
          {!simpleMode && <View style={styles.equipmentRow}>
            {EQUIPMENT_OPTIONS.map((eq) => {
              const isActive = (entry.equipment || 'barbell') === eq.id;
              return (
                <TouchableOpacity
                  key={eq.id}
                  onPress={() => onUpdate(entry.id, { equipment: eq.id })}
                  style={[
                    styles.equipmentBadge,
                    {
                      backgroundColor: isActive ? theme.colors.accent + '20' : theme.colors.surfaceVariant,
                      borderColor: isActive ? theme.colors.accent : theme.colors.borderLight,
                    },
                  ]}>
                  <Text
                    style={[
                      styles.equipmentBadgeText,
                      { color: isActive ? theme.colors.accent : theme.colors.textTertiary },
                    ]}>
                    {eq.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>}

          {!simpleMode && suggestion && (
            <TouchableOpacity
              style={[styles.suggestionChip, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.accent + '40' }]}
              onPress={applySuggestion}
              activeOpacity={0.7}>
              <Text style={[styles.suggestionText, { color: theme.colors.accent }]}>
                {suggestion.suggestedWeight > 0 ? `Try ${suggestion.suggestedWeight}×${suggestion.suggestedReps} today` : `Try ${suggestion.suggestedReps} reps today`}
              </Text>
            </TouchableOpacity>
          )}

          {/* Per-set rows: Weight / Reps / Time (optional) */}
          <View>
              {/* Header row */}
              <View style={styles.setHeaderRow}>
                <Text style={[styles.setHeaderLabel, { color: theme.colors.textTertiary, width: 28 }]}>Set</Text>
                {!isDeadHang && <Text style={[styles.setHeaderLabel, { color: theme.colors.textTertiary, flex: 1 }]}>{isFarmersCarry ? 'Total Load' : 'Weight'} ({isTimeBased && !isFarmersCarry ? '' : userUnits})</Text>}
                <Text style={[styles.setHeaderLabel, { color: theme.colors.textTertiary, flex: 1 }]}>{isTimeBased ? 'Time (sec)' : 'Reps'}</Text>

                <View style={{ width: 28 }} />
              </View>
              {(entry.setDetails && entry.setDetails.length > 0 ? entry.setDetails : [{ weight: entry.weight, reps: entry.reps, time: '' } as SetData]).map((set, setIdx) => {
                const updateSet = (patch: Partial<SetData>) => {
                  const details = [...(entry.setDetails ?? [{ weight: entry.weight, reps: entry.reps, time: '' }])];
                  details[setIdx] = { ...details[setIdx], ...patch };
                  const topWeight = details[0]?.weight ?? entry.weight;
                  const topReps = details[0]?.reps ?? entry.reps;
                  onUpdate(entry.id, { setDetails: details, weight: topWeight, reps: topReps, sets: String(details.length) });
                };
                return (
                  <SetRow
                    key={setIdx}
                    set={set}
                    setIdx={setIdx}
                    isTimeBased={isTimeBased}
                    isFarmersCarry={isFarmersCarry}
                    isDeadHang={isDeadHang}
                    userUnits={userUnits}
                    canRemove={(entry.setDetails?.length ?? 1) > 1}
                    showConfirm={false}
                    isPR={entry.isPR}
                    theme={theme}
                    displayWeight={displayWeight}
                    onUpdateSet={updateSet}
                    onRemoveSet={() => {
                      const details = [...(entry.setDetails ?? [])];
                      details.splice(setIdx, 1);
                      const topWeight = details[0]?.weight ?? '';
                      const topReps = details[0]?.reps ?? '';
                      onUpdate(entry.id, { setDetails: details.length > 0 ? details : undefined, weight: topWeight, reps: topReps, sets: String(Math.max(details.length, 1)) });
                    }}
                    onConfirmSet={onSetLogged}
                  />
                );
              })}
              <TouchableOpacity
                style={[styles.addSetBtn, { borderColor: theme.colors.border }]}
                onPress={() => {
                  const details = [...(entry.setDetails ?? [{ weight: entry.weight, reps: entry.reps }])];
                  // Copy weight from the last set that actually has a weight entered
                  const lastWithWeight = [...details].reverse().find(s => s.weight && parseFloat(s.weight) > 0);
                  const lastSet = details[details.length - 1];
                  details.push({ weight: lastWithWeight?.weight ?? '', reps: lastSet?.reps ?? '' });
                  onUpdate(entry.id, { setDetails: details, sets: String(details.length) });
                }}>
                <Text style={[styles.addSetText, { color: theme.colors.accent }]} accessibilityRole="button" accessibilityLabel="Add set">+ Add Set</Text>
              </TouchableOpacity>
          </View>

          {!simpleMode && (() => {
            const sets = entry.setDetails ?? [{ weight: entry.weight, reps: entry.reps }];
            const totalVol = sets.reduce((sum, s) => {
              const weight = parseFloat(s.weight) || 0;
              const reps = parseInt(s.reps) || 0;
              // Since this is current logging, weights are entered in user's current units
              const convertedWeight = displayWeight(weight, userUnits);
              return sum + convertedWeight * reps;
            }, 0);
            return totalVol > 0 ? (
              <Text style={[styles.volumeText, { color: theme.colors.textSecondary }]}>
                Volume: {Math.round(totalVol * 10) / 10} {userUnits}
              </Text>
            ) : null;
          })()}

          {/* RPE + Notes — available to all tiers (training inputs, not analytics) */}
          {!simpleMode && (
            <View style={styles.proFields}>
              <View style={styles.rpeContainer}>
                <Text style={[styles.rpeLabel, { color: theme.colors.textSecondary }]}>Effort (1-10)</Text>
                <Text style={[styles.rpeSubtitle, { color: theme.colors.textTertiary }]}>How hard did this feel?</Text>
                <View style={styles.rpeRow}>
                  {[1,2,3,4,5,6,7,8,9,10].map((n) => {
                    const isSelected = entry.rpe === String(n);
                    return (
                      <TouchableOpacity
                        key={n}
                        style={[
                          styles.rpeDot,
                          {
                            backgroundColor: isSelected ? theme.colors.accent : theme.colors.surfaceVariant,
                            borderColor: isSelected ? theme.colors.accent : theme.colors.borderLight,
                          },
                        ]}
                        onPress={() => onUpdate(entry.id, { rpe: isSelected ? '' : String(n) })}>
                        <Text style={[styles.rpeNum, { color: isSelected ? theme.colors.text : theme.colors.textTertiary }]}>
                          {n}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
              <TextInput
                style={[styles.notesInput, {
                  backgroundColor: theme.colors.surfaceVariant,
                  borderColor: theme.colors.borderLight,
                  color: theme.colors.text,
                }]}
                value={entry.notes}
                onChangeText={(notes) => onUpdate(entry.id, { notes })}
                placeholder="Notes (optional)"
                placeholderTextColor={theme.colors.textTertiary}
                multiline
                maxLength={500}
                accessibilityLabel="Exercise notes"
              />
            </View>
          )}

          {!simpleMode && profile?.id && entry.exercise && (
            <LiftHistory
              exerciseId={entry.exercise.id}
              exerciseName={entry.exercise.name}
              profileId={profile.id}
            />
          )}

          {!simpleMode && <TouchableOpacity
            style={[
              styles.prToggle,
              {
                backgroundColor: entry.isPR
                  ? theme.colors.surfaceVariant
                  : theme.colors.surface,
                borderColor: entry.isPR ? theme.colors.warning : theme.colors.borderLight,
              },
            ]}
            onPress={() => onUpdate(entry.id, { isPR: !entry.isPR })}
            accessibilityRole="button"
            accessibilityLabel={entry.isPR ? 'Unmark personal record' : 'Mark as personal record'}>
            {entry.isPR && <CheckCircle size={20} color={theme.colors.warning} />}
            <Text
              style={[
                styles.prToggleText,
                {
                  color: entry.isPR
                    ? theme.colors.warning
                    : theme.colors.textSecondary,
                },
              ]}>
              Personal Record (Track PR)
            </Text>
          </TouchableOpacity>}

          {!simpleMode && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <TouchableOpacity
                style={[
                  styles.videoButton,
                  {
                    flex: 1,
                    backgroundColor: entry.videoUri ? theme.colors.accent + '15' : theme.colors.surfaceVariant,
                    borderColor: entry.videoUri ? theme.colors.accent : theme.colors.borderLight,
                  },
                ]}
                onPress={() => onShowVideoOptions(entry.id)}
                accessibilityRole="button"
                accessibilityLabel={entry.videoUri ? 'Video selected, change video' : 'Upload video'}>
                <Video size={20} color={entry.videoUri ? theme.colors.accent : theme.colors.textSecondary} />
                <Text style={[styles.videoButtonText, { color: entry.videoUri ? theme.colors.accent : theme.colors.textSecondary }]}>
                  {entry.videoUri ? '📹 Video Proof Added' : '📹 Add Video Proof'}
                </Text>
              </TouchableOpacity>
              {entry.videoUri && (
                <TouchableOpacity
                  onPress={() => {
                    Alert.alert('Remove Video?', 'This will detach the video from this exercise.', [
                      { text: 'Cancel', style: 'cancel' },
                      { text: 'Remove', style: 'destructive', onPress: () => onUpdate(entry.id, { videoUri: null }) },
                    ]);
                  }}
                  style={{ padding: 8 }}
                  accessibilityRole="button"
                  accessibilityLabel="Remove video">
                  <X size={16} color={theme.colors.error} />
                </TouchableOpacity>
              )}
            </View>
          )}
          </>)}
          {/* End collapsed section */}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  entryCard: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 16,
  },
  cardHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  simpleModeToggle: {
    fontSize: 12,
    fontWeight: '600',
    textDecorationLine: 'underline',
    paddingHorizontal: 4,
    paddingVertical: 2,
  },

  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  selectExerciseButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    borderWidth: 2,
    borderStyle: 'solid',
    borderRadius: 2,
    paddingVertical: 20,
    marginBottom: 8,
  },
  selectExerciseText: {
    fontSize: 16,
    fontWeight: '700',
  },
  selectedExercise: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    padding: 12,
    borderRadius: 2,
    marginBottom: 12,
  },
  selectedExerciseName: {
    fontSize: 16,
    fontWeight: '700',
  },
  exerciseCategory: {
    fontSize: 12,
    marginTop: 2,
    textTransform: 'capitalize',
  },
  exerciseInfoSection: {
    marginBottom: 12,
  },
  exerciseInfoToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  muscleBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 2,
  },
  muscleBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'capitalize',
  },
  exerciseInstructions: {
    fontSize: 13,
    lineHeight: 18,
    marginTop: 8,
  },
  changeText: {
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  lastPerfRow: {
    borderRadius: 2,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  lastPerfLabel: {
    fontSize: 10,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 1,
    textTransform: 'uppercase' as const,
    marginBottom: 2,
  },
  lastPerfText: {
    fontSize: 13,
    fontFamily: 'Inter_600SemiBold',
  },
  lastPerfSuggestion: {
    fontSize: 12,
    fontFamily: 'Inter_600SemiBold',
    marginTop: 2,
  },
  deltaText: {
    fontSize: 14,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 0.5,
    minWidth: 60,
    textAlign: 'right' as const,
  },
  suggestionChip: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 12,
    alignItems: 'center',
  },
  suggestionText: {
    fontSize: 13,
    fontWeight: '600',
  },
  inputRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  inputContainer: {
    flex: 1,
  },
  input: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    textAlign: 'center',
  },
  setHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
    paddingHorizontal: 2,
  },
  setHeaderLabel: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },

  warmupToggle: {
    minWidth: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  warmupToggleActive: {
    paddingHorizontal: 6,
    borderRadius: 4,
  },
  warmupText: {
    fontSize: 11,
    fontWeight: '800',
  },

  addSetBtn: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 2,
    paddingVertical: 8,
    alignItems: 'center',
    marginTop: 4,
    marginBottom: 8,
  },
  addSetText: {
    fontSize: 13,
    fontWeight: '700',
  },

  volumeText: {
    fontSize: 14,
    marginBottom: 12,
    textAlign: 'center',
  },
  prToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    padding: 12,
    borderRadius: 2,
    marginBottom: 8,
  },
  prToggleText: {
    fontSize: 14,
    fontWeight: '600',
  },
  videoButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 2,
    borderStyle: 'solid',
    padding: 16,
    borderRadius: 2,
  },
  videoButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  modeRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  modeBtn: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 8,
    borderRadius: 2,
    borderWidth: 1,
  },
  modeBtnText: {
    fontSize: 13,
    fontWeight: '700',
  },
  proFields: {
    gap: 8,
    marginBottom: 12,
  },
  rpeContainer: {
    gap: 6,
  },
  rpeLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  rpeSubtitle: {
    fontSize: 11,
    fontWeight: '400',
  },
  rpeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 4,
  },
  rpeDot: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    borderRadius: 2,
    borderWidth: 1,
  },
  rpeNum: {
    fontSize: 12,
    fontWeight: '700',
  },
  equipmentRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 12,
  },
  equipmentBadge: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 2,
    borderWidth: 1,
  },
  equipmentBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  rpePill: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderRadius: 2,
    paddingVertical: 8,
    minWidth: 36,
  },
  rpePillText: {
    fontSize: 12,
    fontWeight: '700',
  },
  rpeDropdown: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    borderWidth: 1,
    borderRadius: 2,
    padding: 6,
    marginBottom: 4,
    marginLeft: 44,
  },
  rpeDropdownItem: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 2,
  },
  rpeDropdownText: {
    fontSize: 12,
    fontWeight: '700',
  },

  notesInput: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    minHeight: 40,
    maxHeight: 80,
    textAlignVertical: 'top',
  },
});
